// Accessibility: what a screen reader can actually make of this
// document, and which of the gaps this editor can close.
//
// The check list follows the same ground the PDF/UA rules and Acrobat's
// checker cover, but it is deliberately split by who can fix what:
//
//   - Some gaps are one flag in the file — a missing /Lang, no
//     /DisplayDocTitle, no tab order on a page of form fields. Those
//     get a working "Fix" button.
//   - Some are content decisions no tool can make for you — the actual
//     words of an image's alternate text, a sensible reading order.
//     Those are reported with what to do and where, and nothing
//     pretends to fix them.
//   - Some are already features of this editor reached from elsewhere
//     (OCR for a scanned page, the security dialog for an extraction
//     ban, the comments panel for annotation descriptions). Those link
//     across rather than duplicating the tool.
//
// Saying "0 issues" when a document is untagged would be the worst
// possible outcome here, so the audit reports the untaggable honestly
// and the panel never claims a passing grade the file hasn't earned.

import type { A11yFacts } from "./protocol.ts";

export type { A11yFacts };

/** The overlay's contribution. Annotations the user has added are UI
 *  state, not part of the bytes the worker audited, so the count comes
 *  from the editor rather than the document. */
export interface A11yOverlayFacts {
  /** Annotations that ought to describe themselves (markup, drawings,
   *  shapes, stamps — everything except a note, whose value IS its
   *  description). */
  describable: number;
  withoutDescription: number;
}

export type A11yStatus = "pass" | "fail" | "warn" | "na";

/** A repair this editor can perform on its own. Anything not in here
 *  needs a human decision or another tool. */
export type A11yFixId = "title" | "displayDocTitle" | "lang" | "tabOrder" | "fieldDescriptions";

/** Somewhere else in the editor that already does this job. */
export type A11yGoTo = "ocr" | "security" | "properties" | "comments" | "fonts";

export interface A11yFinding {
  id: string;
  status: A11yStatus;
  title: string;
  /** What the audit found, in plain words. */
  detail: string;
  /** What to do about it. Omitted when it passes. */
  fix?: string;
  fixId?: A11yFixId;
  goTo?: A11yGoTo;
  /** 1-based pages the finding is about, when it is page-specific. */
  pages?: number[];
}

/** A long document with no bookmarks is hard to navigate without sight
 *  of the page thumbnails. Below this it is not worth mentioning. */
const BOOKMARK_THRESHOLD = 10;

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** At most a few page numbers, then "…" — a finding that names 200
 *  pages is a finding nobody reads. */
export function pageList(pages: readonly number[], max = 6): string {
  if (pages.length === 0) return "";
  const head = pages.slice(0, max).join(", ");
  return pages.length > max ? `${head} and ${pages.length - max} more` : head;
}

/** Turn the facts into the check list. Ordered by how much it matters:
 *  a screen reader that cannot read the text at all comes before a
 *  missing window title. */
export function auditAccessibility(
  f: A11yFacts,
  overlay: A11yOverlayFacts = { describable: 0, withoutDescription: 0 }
): A11yFinding[] {
  const out: A11yFinding[] = [];

  // ── can a screen reader get at the content at all? ──

  out.push(
    f.encrypted && !f.extractForAccessibility
      ? {
          id: "extract",
          status: "fail",
          title: "Screen-reader access is blocked",
          detail:
            "This file's permissions forbid extracting text for accessibility, so assistive technology may refuse to read it.",
          fix: "Remove the restriction in Security, then export again.",
          goTo: "security",
        }
      : {
          id: "extract",
          status: "pass",
          title: "Screen-reader access is allowed",
          detail: f.encrypted
            ? "The file is encrypted but permits extracting text for accessibility."
            : "Nothing in the file restricts reading it.",
        }
  );

  if (f.imageOnlyPages.length > 0) {
    out.push({
      id: "scanned",
      status: "fail",
      title: "Some pages are images with no text",
      detail: `${plural(f.imageOnlyPages.length, "page")} draw${
        f.imageOnlyPages.length === 1 ? "s" : ""
      } a picture but hold no text a reader can extract — the shape of a scan (${pageList(
        f.imageOnlyPages
      )}).`,
      fix: "Run OCR to recognise the text and add a real text layer.",
      goTo: "ocr",
      pages: f.imageOnlyPages,
    });
  } else {
    const blank = f.pagesWithoutText;
    out.push(
      blank.length > 0 && blank.length < f.pageCount
        ? {
            id: "scanned",
            status: "warn",
            title: "Some pages hold no text",
            detail: `${plural(blank.length, "page")} extract${
              blank.length === 1 ? "s" : ""
            } no text (${pageList(blank)}). Deliberately blank pages are fine; pages with content are not.`,
            fix: "Check those pages. If they carry words, run OCR.",
            goTo: "ocr",
            pages: blank,
          }
        : {
            id: "scanned",
            status: "pass",
            title: "Text is extractable",
            detail: "Every page with content offers text a reader can extract.",
          }
    );
  }

  out.push(
    f.fontsWithoutUnicode.length > 0
      ? {
          id: "unicode",
          status: "warn",
          title: "Some text may extract as gibberish",
          detail: `${plural(
            f.fontsWithoutUnicode.length,
            "font"
          )} carries neither a /ToUnicode map nor a standard encoding, so its characters have no defined meaning: ${f.fontsWithoutUnicode
            .slice(0, 4)
            .join(", ")}.`,
          fix: "Re-export from the source with fonts embedded, or OCR the affected pages.",
          goTo: "fonts",
        }
      : {
          id: "unicode",
          status: "pass",
          title: "Characters map to Unicode",
          detail: "Every font either maps to Unicode or uses a standard encoding.",
        }
  );

  // ── structure: the part no editor can invent ──

  const tagged = f.marked && f.hasStructTree;
  out.push(
    tagged
      ? {
          id: "tagged",
          status: "pass",
          title: "The document is tagged",
          detail:
            "It carries a structure tree, so a reader can follow headings, paragraphs and reading order.",
        }
      : {
          id: "tagged",
          status: "fail",
          title: "The document is not tagged",
          detail: f.hasStructTree
            ? "There is a structure tree but the document is not marked as tagged, so readers will ignore it."
            : "There is no structure tree, so nothing tells a reader which text is a heading, where a paragraph begins, or what order to read in.",
          fix: "Tagging is not yet something this editor can add. Until then, tag the file in the application it came from, or in a tool that can auto-tag.",
        }
  );

  if (f.imageCount > 0 || f.figureCount > 0) {
    const missing = Math.max(0, f.figureCount - f.figuresWithAlt);
    if (!tagged) {
      out.push({
        id: "imageAlt",
        status: "fail",
        title: "Images have no alternate text",
        detail: `${plural(
          f.imageCount,
          "image"
        )} in this document, and alternate text lives in the structure tree — an untagged document has nowhere to put it.`,
        fix: "Tag the document first — until then no image can be described.",
      });
    } else if (f.figureCount === 0) {
      out.push({
        id: "imageAlt",
        status: "warn",
        title: "Images are not marked up as figures",
        detail: `${plural(
          f.imageCount,
          "image"
        )} is drawn on the page but the structure tree has no Figure element for it, so there is nothing to attach a description to.`,
        fix: "Tag the images as figures in a tagging tool.",
      });
    } else if (missing > 0) {
      out.push({
        id: "imageAlt",
        status: "warn",
        title: "Some figures have no alternate text",
        detail: `${missing} of ${plural(
          f.figureCount,
          "figure"
        )} carries no description, so a reader can only say that a picture is there.`,
        fix: "Add alternate text to each — in a tagging tool, or by giving the source document alt text before importing it.",
      });
    } else {
      out.push({
        id: "imageAlt",
        status: "pass",
        title: "Every figure has alternate text",
        detail: `All ${plural(f.figureCount, "figure")} ${
          f.figureCount === 1 ? "carries" : "carry"
        } a description. Whether each one is a GOOD description is not something this can tell you — a file name is not a description of a photograph.`,
      });
    }
  }

  // ── the flags this editor CAN set ──

  out.push(
    f.title.trim()
      ? {
          id: "title",
          status: "pass",
          title: "The document has a title",
          detail: `“${f.title.trim()}”`,
        }
      : {
          id: "title",
          status: "fail",
          title: "The document has no title",
          detail:
            "Readers and screen readers fall back to the file name, which is rarely a useful description.",
          fix: "Set a title.",
          fixId: "title",
        }
  );

  out.push(
    !f.title.trim()
      ? {
          id: "displayDocTitle",
          status: "na",
          title: "Window title",
          detail: "Nothing to show until the document has a title.",
        }
      : f.displayDocTitle
        ? {
            id: "displayDocTitle",
            status: "pass",
            title: "The title is shown, not the file name",
            detail: "Viewers announce the document's title in the window bar.",
          }
        : {
            id: "displayDocTitle",
            status: "fail",
            title: "Viewers will show the file name, not the title",
            detail:
              "Without /DisplayDocTitle a viewer labels the window with the file name, so the title never reaches the user.",
            fix: "Show the title instead.",
            fixId: "displayDocTitle",
          }
  );

  out.push(
    f.lang
      ? {
          id: "lang",
          status: "pass",
          title: "The document declares its language",
          detail: `/Lang is “${f.lang}”, which tells a screen reader how to pronounce the text.`,
        }
      : {
          id: "lang",
          status: "fail",
          title: "The document does not declare a language",
          detail:
            "A screen reader has to guess which language to pronounce the text in, and guesses badly.",
          fix: "Set the document language.",
          fixId: "lang",
        }
  );

  // ── forms ──

  if (f.widgetCount === 0) {
    out.push({
      id: "fieldDescriptions",
      status: "na",
      title: "Form fields",
      detail: "This document has no form fields.",
    });
  } else if (f.fieldsWithoutDescription.length > 0) {
    out.push({
      id: "fieldDescriptions",
      status: "fail",
      title: "Some form fields have no description",
      detail: `${f.fieldsWithoutDescription.length} of ${plural(
        f.widgetCount,
        "field"
      )} carry no tooltip, so a screen reader announces nothing but the field type.`,
      fix: "Fill each field's description from its name as a starting point, then reword the ones that read badly.",
      fixId: "fieldDescriptions",
    });
  } else {
    out.push({
      id: "fieldDescriptions",
      status: "pass",
      title: "Every form field has a description",
      detail: `All ${plural(f.widgetCount, "field")} carry a tooltip a reader can announce.`,
    });
  }

  if (f.pagesWithoutTabOrder.length > 0) {
    out.push({
      id: "tabOrder",
      status: "fail",
      title: "Form fields have no defined tab order",
      detail: `On ${plural(
        f.pagesWithoutTabOrder.length,
        "page"
      )} the fields have no /Tabs entry, so keyboard users get whatever order the file happens to store them in (${pageList(
        f.pagesWithoutTabOrder
      )}).`,
      fix: "Set tab order to follow the document's structure.",
      fixId: "tabOrder",
      pages: f.pagesWithoutTabOrder,
    });
  } else if (f.widgetCount > 0) {
    out.push({
      id: "tabOrder",
      status: "pass",
      title: "Tab order is defined",
      detail: "Every page of fields declares how the keyboard should move through it.",
    });
  }

  // ── annotations ──

  const missingAnnots = f.docAnnotsWithoutDescription + overlay.withoutDescription;
  const totalAnnots = f.docAnnotsWithoutDescription + overlay.describable;
  if (totalAnnots === 0) {
    out.push({
      id: "annotDescriptions",
      status: "na",
      title: "Annotation descriptions",
      detail: "There are no annotations that need describing.",
    });
  } else {
    out.push(
      missingAnnots > 0
        ? {
            id: "annotDescriptions",
            status: "warn",
            title: "Some annotations have no description",
            detail:
              missingAnnots === 1
                ? "One annotation would be announced as a bare shape — a highlight or an arrow means nothing without words."
                : `${missingAnnots} annotations would be announced as bare shapes — a highlight or an arrow means nothing without words.`,
            fix: "Add a comment to each in the Comments panel; the comment IS the description.",
            goTo: "comments",
          }
        : {
            id: "annotDescriptions",
            status: "pass",
            title: "Every annotation is described",
            detail: "Each one carries a comment a reader can announce.",
          }
    );
  }

  // ── navigation ──

  if (f.pageCount >= BOOKMARK_THRESHOLD) {
    out.push(
      f.outlineCount > 0
        ? {
            id: "bookmarks",
            status: "pass",
            title: "The document has bookmarks",
            detail: `${plural(f.outlineCount, "bookmark")} give a keyboard user a way around.`,
          }
        : {
            id: "bookmarks",
            status: "warn",
            title: "A long document with no bookmarks",
            detail: `${f.pageCount} pages and no outline, so there is no way to jump to a section without scrolling.`,
            fix: "Add bookmarks in the Bookmarks panel.",
          }
    );
  }

  return out;
}

export interface A11yScore {
  fail: number;
  warn: number;
  pass: number;
  /** Checks that had something to say (na excluded). */
  applicable: number;
}

export function a11yScore(findings: readonly A11yFinding[]): A11yScore {
  const score: A11yScore = { fail: 0, warn: 0, pass: 0, applicable: 0 };
  for (const f of findings) {
    if (f.status === "na") continue;
    score.applicable += 1;
    score[f.status] += 1;
  }
  return score;
}

/** A one-line verdict for the panel header. Deliberately never says
 *  "accessible" — a machine can only rule things out. */
export function a11ySummary(score: A11yScore): string {
  if (score.applicable === 0) return "Nothing to check yet";
  if (score.fail === 0 && score.warn === 0) {
    return `All ${score.applicable} automated checks pass — a human still has to read it`;
  }
  const parts: string[] = [];
  if (score.fail > 0) parts.push(`${score.fail} to fix`);
  if (score.warn > 0) parts.push(`${score.warn} to look at`);
  return `${parts.join(", ")} of ${score.applicable} checks`;
}

/** Languages offered by the /Lang picker: BCP-47 tags, common first.
 *  The field takes any tag — this is a shortcut, not a whitelist. */
export const A11Y_LANGUAGES: readonly { code: string; label: string }[] = [
  { code: "en-AU", label: "English (Australia)" },
  { code: "en-GB", label: "English (United Kingdom)" },
  { code: "en-US", label: "English (United States)" },
  { code: "ar", label: "Arabic" },
  { code: "zh-Hans", label: "Chinese (Simplified)" },
  { code: "zh-Hant", label: "Chinese (Traditional)" },
  { code: "nl", label: "Dutch" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "hi", label: "Hindi" },
  { code: "id", label: "Indonesian" },
  { code: "it", label: "Italian" },
  { code: "ja", label: "Japanese" },
  { code: "ko", label: "Korean" },
  { code: "pt-BR", label: "Portuguese (Brazil)" },
  { code: "ru", label: "Russian" },
  { code: "es", label: "Spanish" },
  { code: "th", label: "Thai" },
  { code: "vi", label: "Vietnamese" },
];

/** A field's own name turned into something worth reading aloud:
 *  "billing.postal_code" → "Postal code", "dateOfBirth" → "Date of
 *  Birth". This is a starting point for a description, not a substitute
 *  for writing one, and the panel says so — a name that was never meant
 *  for human eyes will still read like one. */
export function describeFieldName(name: string): string {
  const leaf = name.split(".").pop() ?? name;
  const words = leaf
    .replace(/[_\-]+/g, " ")
    // camelCase and ACRONYMWord both become word boundaries.
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return name;
  const small = new Set(["of", "the", "and", "for", "to", "in", "a", "an", "or"]);
  const out = words.map((w, i) => (i > 0 && small.has(w.toLowerCase()) ? w.toLowerCase() : w));
  const first = out[0]!;
  out[0] = first.charAt(0).toUpperCase() + first.slice(1);
  return out.join(" ");
}
